import type { SearchResult } from '../../types/dictionary'
import { SearchResultItem } from '../search/search-result-item'
import { MinorHeading } from '@/components/ui/typography'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import { useWordSearch } from '@/hooks/useWordSearch'
import { useSettings } from '@/hooks/useSettings'
import { IconLoader2 } from '@tabler/icons-react'

interface Props {
  entry: SearchResult
  onSelectEntry?: (entry: { id: number }) => void
}

export function WordsTab({ entry, onSelectEntry }: Props) {
  const { simplified, traditional } = entry
  const { settings } = useSettings()

  // Determine primary script based on user settings (same logic as entry-detail.tsx)
  const isTraditionalForSearch =
    settings.scriptDisplay === 'traditional' ||
    (settings.scriptDisplay === 'both' &&
      settings.scriptPriority === 'traditional')
  const searchText =
    isTraditionalForSearch && traditional && traditional !== simplified
      ? traditional
      : simplified

  const { data: wordData, isLoading } = useWordSearch(searchText)

  const handleItemClick = (item: SearchResult) => {
    onSelectEntry?.({ id: item.id })
  }

  // Show loading only if we have no data at all (first load)
  // If we have data but are fetching, show the data (placeholderData handles this)
  const showLoading = isLoading && !wordData

  return (
    <div className="space-y-8">
      {showLoading ? (
        <div className="flex items-center justify-center py-8">
          <IconLoader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : (
        <>
          {wordData &&
          (wordData.beginsWith?.length > 0 || wordData.contains?.length > 0) ? (
            <Accordion
              type="multiple"
              defaultValue={['beginsWith', 'contains']}
            >
              {/* Words Beginning */}
              {wordData.beginsWith && wordData.beginsWith.length > 0 && (
                <AccordionItem value="beginsWith">
                  <AccordionTrigger>
                    <MinorHeading className="text-xs tracking-wider uppercase">
                      Words Beginning with &quot;{searchText}&quot;
                    </MinorHeading>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1">
                      {wordData.beginsWith.slice(0, 20).map((item, i) => (
                        <SearchResultItem
                          key={i}
                          item={item}
                          onClick={handleItemClick}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Words Containing */}
              {wordData.contains && wordData.contains.length > 0 && (
                <AccordionItem value="contains">
                  <AccordionTrigger>
                    <MinorHeading className="text-xs tracking-wider uppercase">
                      Words Containing &quot;{searchText}&quot;
                    </MinorHeading>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1">
                      {wordData.contains.slice(0, 20).map((item, i) => (
                        <SearchResultItem
                          key={i}
                          item={item}
                          onClick={handleItemClick}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground text-sm">
                No words found for &quot;{searchText}&quot;
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
